import {css, LitElement, ReactiveController} from 'lit';
// @ts-ignore
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import {SpImageService} from "../services/sp-image.service";

export class SpPhotoswipeController implements ReactiveController {
    host: LitElement;
    imageGallery: PhotoSwipeLightbox;
    imageGalleryId;
    tempImageObjectUrls = [];
    imageGalleryImages = [];
    imagesLoading = false;
    suppressUrlState = false;
    hiddenElements;
    popStateHandler = this.syncStateWithQueryParams.bind(this);

    /**
     * @param host The lit element to apply the controller to
     * @param options Options to customize photoswipe behavior
     * @param options.initialImages The array with initial images for the gallery
     * @param options.openFromElement A callback that provides the Element to apply the open / close animation on for a given index
     * @param options.hideOnOpen A callback that defines which other Elements should be hidden when a given index is open in gallery (before open animation starts)
     */
    constructor(
        host: LitElement,
        options?: {
            initialImages?: SpPhotoSwipeImage[],
            openFromElement?: (index: number) => HTMLElement,
            hideOnOpen?: (index: number) => Element[],
            galleryId?: string,
            // Suppress attachment of a searchParam to the route on gallery open
            suppressUrlState?: boolean
        }
    ) {
        (this.host = host).addController(this);
        this.imageGalleryId = options?.galleryId ?? 'default';
        if (options?.suppressUrlState)
            this.suppressUrlState = true;
        this.imageGallery = new PhotoSwipeLightbox({
            tapAction: (pt, evt) => {
                if ((evt.target as Element).classList.contains('pswp__img')) {
                    this.imageGallery.pswp.next();
                } else {
                    this.imageGallery.pswp.close();
                }
            },
            doubleTapAction: false,
            pswpModule: () => import('photoswipe'),
        });
        this.imageGallery.addFilter('numItems', (numItems) => {
            return this.imageGalleryImages?.length ? this.imageGalleryImages.length : 0;
        });
        this.imageGallery.addFilter('itemData', (itemData, index) => this.imageGalleryImages[index]);
        this.imageGallery.addFilter('thumbEl', (thumbEl, data, index) => {
            // When no dimensions are present, the image couldn't be loaded. When that is the case, provide no thumbElement to avoid animation on error message
            return this.imageGalleryImages[index].width && this.imageGalleryImages[index].height && options?.openFromElement ?
                options?.openFromElement(index) :
                null;
        });
        this.imageGallery.addFilter('isContentLoading', (isContentLoading, content) => {
            return this.imagesLoading;
        });
        this.imageGallery.on('change', () => {
            this.unhideElements();
            const hideRefs = options?.hideOnOpen ? options.hideOnOpen(this.imageGallery.pswp.currIndex) : [];
            if (hideRefs && hideRefs.length > 0) {
                this.hiddenElements = hideRefs;
                for (const ref of hideRefs) {
                    ref.classList.add('sp-photoswipe-hidden');
                }
            }
            this.host.dispatchEvent(new CustomEvent('photoswipe-change', { detail: {
                index: this.imageGallery.pswp.currIndex,
                length: this.imageGalleryImages.length
            } }));
            const url = new URL(window.location.href);
            if (!this.suppressUrlState && (!this.galleryInQueryParams(url) || !this.positionIsSetInQueryParams(url, this.imageGallery.pswp.currIndex))) {
                url.searchParams.delete('sp-gallery');
                url.searchParams.append('sp-gallery', this.imageGalleryId);
                url.searchParams.delete('sp-gallery-position');
                url.searchParams.append('sp-gallery-position', this.imageGallery.pswp.currIndex.toString());
                window.history.pushState("", document.title, url);
            }
        });
        this.imageGallery.on('afterInit', () => {
            this.host.dispatchEvent(new CustomEvent('photoswipe-will-open', { detail: {
                atIndex: this.imageGallery.pswp.currIndex,
                length: this.imageGalleryImages.length
            } }));
        });
        this.imageGallery.on('destroy', () => {
            const url = new URL(window.location.href);
            if (!this.suppressUrlState && this.galleryInQueryParams(url)) {
                url.searchParams.delete('sp-gallery');
                url.searchParams.delete('sp-gallery-position');
                window.history.pushState("", document.title, url);
            }
            this.host.dispatchEvent(new CustomEvent('photoswipe-closed'));
            this.unhideElements();
        });
        this.imageGallery.init;
        if (options?.initialImages) this.updateData(options.initialImages);
        return this;
    }

    async updateData(images: SpPhotoSwipeImage[]) {
        this.imagesLoading = true;
        // Revoke old objectUrls
        for (const imageObjectUrl of this.tempImageObjectUrls) {
            URL.revokeObjectURL(imageObjectUrl);
        }
        // Convert Blobs to objectUrls
        const convertedImages = images.map((img, index) => {
            let fullSize = images[index].fullSize;
            if (typeof fullSize !== 'string') {
                images[index].fullSize = URL.createObjectURL(fullSize);
                this.tempImageObjectUrls.push(images[index].fullSize);
            }
            let thumb = images[index].thumb;
            if (typeof thumb !== 'string') {
                images[index].thumb = URL.createObjectURL(thumb);
                this.tempImageObjectUrls.push(images[index].thumb);
            }
            return img;
        });
        this.imageGalleryImages =
            (await SpImageService.getImagesDimensions(convertedImages.map(image => image.fullSize)))
                .map((imgDimensions, index) => {
                    return {
                        ...imgDimensions,
                        src: convertedImages[index].fullSize,
                        msrc: convertedImages[index].thumb,
                        thumbCropped: true
                    }
                });
        if (!this.suppressUrlState)
            this.syncStateWithQueryParams();
        this.imagesLoading = false;
    }

    open(atPosition = 0) {
        this.imageGallery.loadAndOpen(atPosition);
    }

    close() {
        if (this.imageGallery.pswp.isOpen)
            this.imageGallery.pswp.close();
    }

    hostConnected() {
        this.host.shadowRoot.adoptedStyleSheets = [ ...this.host.shadowRoot.adoptedStyleSheets, css`.sp-photoswipe-hidden { opacity: 0 }`.styleSheet ];
        // Check if url points to gallery and open / close if yes / no
        if (!this.suppressUrlState)
            window.addEventListener('popstate', this.popStateHandler);
    }

    hostDisconnected() {
        if (!this.suppressUrlState)
         window.removeEventListener('popstate', this.popStateHandler);
    }

    private syncStateWithQueryParams() {
        const url = new URL(window.location.href);
        if (this.imageGallery.pswp?.isOpen && !this.galleryInQueryParams(url)) {
            this.imageGallery.pswp.close();
        } else if (!this.imageGallery.pswp?.isOpen && this.galleryInQueryParams(url)) {
            const openAt = url.searchParams.has('sp-gallery-position') ? +url.searchParams.get('sp-gallery-position') : 0;
            if (this.imageGalleryImages.length > openAt)
                this.open(openAt);
        } else if (this.imageGallery.pswp?.isOpen && !this.positionIsSetInQueryParams(url, this.imageGallery.pswp.currIndex)) {
            this.imageGallery.pswp.goTo(url.searchParams.has('sp-gallery-position') ? +url.searchParams.get('sp-gallery-position') : 0);
        }
    }

    private unhideElements() {
        if (this.hiddenElements) {
            for (const ref of this.hiddenElements) {
                ref.classList.remove('sp-photoswipe-hidden');
            }
            this.hiddenElements = null;
        }
    }

    private galleryInQueryParams(url: URL) {
        return url.searchParams.has('sp-gallery') && url.searchParams.get('sp-gallery') === this.imageGalleryId;
    }

    private positionIsSetInQueryParams(url: URL, position: number) {
        return url.searchParams.has('sp-gallery-position') && url.searchParams.get('sp-gallery-position') === position.toString();
    }
}

export interface SpPhotoSwipeImage {
    fullSize: Blob | string,
    thumb?: Blob | string
}